"use client";

import { useSidebar } from "@/components/ui/sidebar";
import { useBreadcrumbs } from "@/hooks/use-breadcrumbs";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { useBillingUIUpgrades } from "@/lib/flags/use-billing-ui-upgrades";
import { routes } from "@/lib/navigation/routes";
import { IconMenu } from "@teral/icons";
import { Logo } from "@teral/ui";
import { cn } from "cn";
import Link from "next/link";
import { Fragment } from "react";
import { CrumbForDescriptor, crumbKey } from "../top-nav";
import { TopNavFeedbackButton } from "../top-nav/feedback-button";
import { HelpButton } from "../top-nav/help-button";
import { UserButton } from "../top-nav/user-button";
import { SectionTabs } from "./section-tabs";
import { UsageMeter } from "./usage-meter";

/**
 * The app bar, drawn like the window bars on teral.dev: the mark, where you are
 * as `workspace / project / app`, the pages of that place as tabs, and the
 * workspace tools on the right. Below very wide screens the tabs take a second row, so none is ever cut off.
 */
export function AppBar({ showSections = true }: { showSections?: boolean }) {
  const workspace = useWorkspaceNavigation();
  const crumbs = useBreadcrumbs();
  const { setOpenMobile } = useSidebar();
  const billingUpgrades = useBillingUIUpgrades();

  return (
    <header className="sticky top-0 z-40 w-full shrink-0 border-b bg-raised/90 backdrop-blur-md backdrop-saturate-150">
      <div className="flex min-h-13 flex-wrap items-center gap-x-4 px-4 md:px-5 2xl:flex-nowrap">
        <div className="flex h-13 min-w-0 flex-1 items-center gap-3 2xl:flex-none">
          <Link
            href={routes.workspaces.overview({ workspaceSlug: workspace.slug })}
            aria-label="Teral"
            className="flex shrink-0 items-center rounded-md outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--focus-ring)"
          >
            <Logo className="h-3.5 w-auto text-gray-12" />
          </Link>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-grayA-5" />
          <ol
            className={cn(
              "flex min-w-0 items-center gap-0.5",
              // Like the site: earlier crumbs quiet, the current one in ink.
              "[&_a]:font-normal [&_a]:text-gray-11 [&>li:last-child_a]:font-medium [&>li:last-child_a]:text-gray-12",
            )}
          >
            {crumbs.map((descriptor, i) => (
              <Fragment key={crumbKey(descriptor)}>
                {i > 0 ? (
                  <li aria-hidden="true" className="px-0.5 text-sm text-gray-8">
                    /
                  </li>
                ) : null}
                <li className={cn("flex min-w-0", i === 0 && crumbs.length > 2 && "max-sm:hidden")}>
                  <CrumbForDescriptor descriptor={descriptor} />
                </li>
              </Fragment>
            ))}
          </ol>
        </div>

        {showSections ? (
          <div className="order-last -mx-4 w-[calc(100%+2rem)] min-w-0 md:-mx-5 md:w-[calc(100%+2.5rem)] 2xl:order-none 2xl:mx-0 2xl:w-auto 2xl:flex-1">
            <SectionTabs />
          </div>
        ) : (
          <div className="hidden flex-1 2xl:block" />
        )}

        <div className="flex h-13 shrink-0 items-center gap-1">
          {billingUpgrades ? <UsageMeter /> : null}
          <TopNavFeedbackButton className="hidden md:flex" />
          <HelpButton />
          <UserButton />
          <button
            type="button"
            onClick={() => setOpenMobile(true)}
            aria-label="Open navigation"
            className="flex size-8 items-center justify-center rounded-md text-gray-10 hover:bg-grayA-3 hover:text-gray-12 md:hidden"
          >
            <IconMenu className="size-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
